<template>
  <div class="switcher-dropdown w-full flex flex-col py-1">
    <!-- Search Bar -->
    <div v-if="lenses.length" class="w-full flex items-center gap-1 pl-2 pr-1.5 py-0.5">
      <div class="w-6 h-6 flex-shrink-0 flex items-center justify-center">
        <SearchIcon className="text-neutral-alpha-650" />
      </div>
      <input ref="searchInput" v-model="search" class="search-input flex-grow min-w-0 max-w-none" placeholder="Search Lens Accounts"
      @keydown="handleKeyboardNavigation" />
    </div>
    <!-- divider -->
    <div v-if="lenses.length" class="w-full h-0 mt-1 border-t border-neutral-alpha-50" />
    <!-- Accounts List -->
    <div v-if="lenses.length" class="accounts-list w-full overflow-hidden" :style="{'--list-height': `${accountsListHeight}px`}">
      <div v-if="!searchResults.length" class="w-full py-2.5 px-3.5">
        <BaseText type="body" size="xs" class="text-neutral-alpha-400">
          No results found
        </BaseText>
      </div>
      <div v-else ref="accounts-scroller" class="scroller w-full flex flex-col py-1.5 px-1 overflow-y-auto" 
      :style="{maxHeight: `${MAX_ACCOUNTS_LIST_HEIGHT}px`}">
        <button v-for="lens in searchResults" :key="`switcher-item-${lens.id}`" :ref="`lens-account-${lens.id}`"
        class="w-full flex items-center gap-2 p-1.5 rounded-md transition-colors min-w-0 duration-100"
        :class="[currentNavItem?.id === lens.id ? 'bg-neutral-alpha-50' : 'hover:bg-neutral-alpha-50', {'opacity-50 pointer-events-none': !lens.isDataReady}]"
        @click="routeToLens(lens.id)" :disabled="!lens.isDataReady">
          <LensAvatar :width="20" :height="20" :avatar-src="lens.avatar" :lens-name="lens.name" font-size="xs" />
          <BaseText type="body" size="sm" class="text-neutral-alpha-900 truncate">
            {{ lens.name }}
          </BaseText>
          <RefreshIcon v-if="!lens.isDataReady" class="text-neutral-alpha-600 flex-shrink-0 ml-auto" />
        </button>
      </div>
    </div>
    <!-- divider -->
    <div v-if="lenses.length" class="w-full h-0 mb-1 border-t border-neutral-alpha-50" />
    <!-- Create New Lens Button -->
    <div class="w-full px-1">
      <button class="group w-full flex items-center gap-2 p-1.5 rounded-md transition-colors duration-100"
      :class="currentNavItem?.id === 'create-new-lens' ? 'bg-neutral-alpha-50' : 'hover:bg-neutral-alpha-50'"
      ref="create-new-lens" @click="openLensCreationModal">
        <LensIcon class="text-neutral-alpha-650 transition-colors duration-100 group-hover:text-neutral-alpha-800 flex-shrink-0" />
        <BaseText type="body" size="sm" class="text-neutral-alpha-900">
          Create New Lens
        </BaseText>
      </button>
    </div>
  </div>
</template>

<script>
import { mapMutations } from 'vuex'
import getTabRouteName from '../../utils/lens/dashboardTabRouteMap'

import LensAvatar from './LensAvatar.vue'
import SearchIcon from '../globals/Icons/SearchIcon.vue'
import LensIcon from '../globals/Icons/SidebarIcons/LensIcon.vue'
import RefreshIcon from '../globals/Icons/LensIcons/RefreshIcon.vue'

const MAX_ACCOUNTS_LIST_HEIGHT = 300 // px

export default {
  name: 'LensSwitcherDropdown',
  components: {
    LensAvatar,
    SearchIcon,
    LensIcon,
    RefreshIcon
  },
  props: {
    currentLensId: {
      type: String,
      required: true
    },
    lenses: {
      type: Array,
      required: true
    }
  },
  data () {
    return {
      search: '',
      navIndex: null,
      MAX_ACCOUNTS_LIST_HEIGHT
    }
  },
  mounted () {
    this.$nextTick(() => {
      this.$refs.searchInput.focus()
    })
  },
  computed: {
    searchResults () {
      let searchResults = []
      this.lenses.forEach(lens => {
        if (lens.id !== this.currentLensId && lens.name.toLowerCase().trim().includes(this.search.toLowerCase().trim())) {
          searchResults.push(lens)
        }
      })
    
      // Sort by closeness of match
      return searchResults.sort((a, b) => {
        const ratioA = this.search.length / a.name.length
        const ratioB = this.search.length / b.name.length
        return ratioB - ratioA
      })
    },
    accountsListHeight () {
      if (!this.searchResults.length) return 36
      const compHeight = this.searchResults.length * 32 + 12 // 12px for y-padding
      return Math.min(compHeight, MAX_ACCOUNTS_LIST_HEIGHT)
    },
    navigationItems () {
      const lensAccounts = this.searchResults.filter(lens => lens.isDataReady).map(lens => ({
        id: lens.id,
        ref: `lens-account-${lens.id}`,
        action: () => this.routeToLens(lens.id)
      }))
      return [
        ...lensAccounts,
        {
          id: 'create-new-lens',
          ref: 'create-new-lens',
          action: this.openLensCreationModal
        }
      ]
    },
    currentNavItem () {
      return this.navigationItems?.[this.navIndex] ?? null
    }
  },
  methods: {
    ...mapMutations('LensModule', ['SET_SHOW_LENS_CREATION_MODAL']),
    routeToLens (lensId) {
      console.log('routeToLens', lensId)
      // If applicable, get the LensDashboard tab name from the url
      let routeName = 'LensDashboardView'
      const currentPath = window.location.pathname
      console.log('currentPath', currentPath)
      console.log('test getRouteName', getTabRouteName('reports'))
      if (currentPath.match(/^\/lens\/([^\/]+)(?:\/(.*))?$/)) {
        const tab = currentPath.split('/').pop()
        console.log('tab', tab)
        routeName = getTabRouteName(tab)
      } else {
        console.log('path didnt match regex')
      }

      this.$router.push({ name: routeName, params: { lensId } })
      this.$emit('close')
    },
    openLensCreationModal () {
      this.$router.push({ name: 'LensView' }).catch(err => {
        console.log('Error pushing to LensView', err)
      })
      this.SET_SHOW_LENS_CREATION_MODAL(true)
      this.$emit('close')
    },
    // ======================== KEYBOARD NAVIGATION ========================
    handleKeyboardNavigation (event) {
      const validKeys = ['ArrowUp', 'ArrowDown', 'Enter', 'Escape', 'Tab']
      if (!validKeys.includes(event.key)) return
      event.preventDefault()
      switch (event.key) {
        case "ArrowDown":
          this.navigateNext(); break
        case "ArrowUp":
          this.navigatePrev(); break
        case "Enter":
          this.selectNavItem(); break
        case "Escape":
          this.$emit('close'); break
      }
    },
    navigateNext () {
      if (this.navIndex === null || this.navIndex < this.navigationItems.length - 1) {
        this.navIndex = this.navIndex === null ? 0 : this.navIndex + 1
        this.scrollToItem(this.currentNavItem.ref)
      }
    },
    navigatePrev () {
      if (this.navIndex === 0) {
        this.navIndex = null
      } else if (this.navIndex !== null) {
        this.navIndex--
        this.scrollToItem(this.currentNavItem.ref)
      }
    },
    selectNavItem () {
      if (this.currentNavItem) this.currentNavItem.action()
    },
    scrollToItem (ref) {
      if (ref === 'create-new-lens') return
      this.$nextTick(() => {
        let item = this.$refs[ref][0]
        if (!item) return

        if (item.$el) item = item.$el
        const itemRect = item.getBoundingClientRect()
        const container = this.$refs['accounts-scroller']
        const containerRect = container.getBoundingClientRect()

        let scrollPosition = container.scrollTop
        const offset = 10
        const overflowTop = containerRect.top - itemRect.top
        const overflowBottom = itemRect.bottom - containerRect.bottom
        if (this.navIndex === 0) {
          scrollPosition = 0
        } else if (this.navIndex === this.navigationItems.length - 1) {
          scrollPosition = container.scrollHeight
        } else if (overflowTop > 0) {
          scrollPosition = container.scrollTop - overflowTop - offset
        } else if (overflowBottom > 0) {
          scrollPosition = container.scrollTop + overflowBottom + offset
        }
        container.scrollTo({ top: scrollPosition, behavior: 'smooth' })
      })
    }
  }
}
</script>

<style scoped>
.switcher-dropdown {
  border-radius: 10px;
  background-color: #23242c;
}
.accounts-list {
  height: var(--list-height);
  animation: listEnter 100ms ease-in-out forwards;
}
@keyframes listEnter {
  from { height: 6px; }
  to { height: var(--list-height); }
}
.scroller::-webkit-scrollbar {
  width: 3px;
}
.scroller::-webkit-scrollbar-thumb {
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 18px;
}

.search-input {
  color: white;
  caret-color: white;

  /* Remove default browser styles */
  -webkit-appearance: none;  /* Remove default styling in WebKit browsers */
  -moz-appearance: none;     /* Remove default styling in Firefox */
  appearance: none;          /* Remove default styling in modern browsers */
  padding: 0;
  margin: 0;
  border: none;
  background-color: transparent;
  outline: none;

  /* Body/Small */
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px; /* 142.857% */
}
.search-input:focus {
  /* Remove default browser styles */
  outline: none;
  border: none;
  box-shadow: none;
}
.search-input::placeholder {
  color: rgba(255, 255, 255, 0.28);
}
</style>